import { FC } from 'react';
import { Field, Select } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import { isValidInstagramUsername } from 'app/modules/posts/modules/post/utils/instagramCollaborator';

type Props = {
  onChange: (collaborators: string[]) => void;
  value: string[];
};

const MAX_COLLABORATORS = 3;

const PostComposerContentInstagramCollaborators: FC<Props> = ({
  onChange,
  value,
}) => {
  const { t } = useTranslation();

  const selectOptions = value.map((username) => ({
    value: username,
    label: `@${username}`,
  }));

  const handleChange = (newValues: any) => {
    const updatedValues = newValues
      ? newValues.map((option: any) => option.value)
      : [];
    onChange(updatedValues);
  };

  const formatCreateLabel = (inputValue: string) => {
    const username = inputValue.startsWith('@')
      ? inputValue.substring(1)
      : inputValue;
    return `${t('add')} "@${username}"`;
  };

  const isValidNewOption = (inputValue: string) =>
    isValidInstagramUsername(inputValue) && value.length < MAX_COLLABORATORS;

  const getNewOptionData = (inputValue: string) => {
    const username = inputValue.startsWith('@')
      ? inputValue.substring(1)
      : inputValue;
    return {
      value: username,
      label: `@${username}`,
    };
  };

  return (
    <Field.Group>
      <Field.Label required>{t('collaborators')}</Field.Label>
      <div
        data-name="posts-composer_post-content_instagram-collaborators"
        className="tw-h-full tw-min-h-[2.5rem]"
      >
        <Select
          isMulti
          isClearable
          isCreatable
          components={{
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
          }}
          placeholder={
            value.length >= MAX_COLLABORATORS
              ? t('instagramCollaborators.maxCollaborators')
              : t('instagramCollaborators.addCollaborator')
          }
          value={selectOptions}
          onChange={handleChange}
          isValidNewOption={isValidNewOption}
          formatCreateLabel={formatCreateLabel}
          getNewOptionData={getNewOptionData}
          noOptionsMessage={({ inputValue }) => {
            if (value.length >= MAX_COLLABORATORS) {
              return t('instagramCollaborators.maxCollaborators');
            }

            // Without this the entry is silently refused and the field looks broken.
            if (inputValue && !isValidInstagramUsername(inputValue)) {
              return t('instagramCollaborators.invalidUsername');
            }

            return t('instagramCollaborators.addCollaboratorPlaceholder');
          }}
          styles={{
            menu: (base) => ({
              ...base,
              zIndex: 10,
            }),
          }}
        />
        {value.length > 0 && value.length < MAX_COLLABORATORS && (
          <div className="tw-mt-1 tw-text-xs tw-text-grayscale-120">
            {t('instagramCollaborators.moreCollaboratorsAllowed', {
              count: MAX_COLLABORATORS - value.length,
            })}
          </div>
        )}
      </div>
      <Field.Caption>
        {t('instagramCollaborators.collaboratorsInfo')}
      </Field.Caption>
    </Field.Group>
  );
};

export default PostComposerContentInstagramCollaborators;
